// src/pages/register/business/steps/BusinessStepTwo/BusinessStepTwo.tsx
import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useRegistration } from '../../../../contexts/registration/hooks';
import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';

interface BusinessStepTwoProps {
  loading?: boolean;
}

const BUSINESS_TYPES = [
  { id: 'hair_salon',    label: 'מספרה' },
  { id: 'beauty_salon',  label: 'מכון יופי' },
  { id: 'nail_salon',    label: 'מכון ציפורניים' },
  { id: 'spa',           label: 'ספא' },
  { id: 'barber',        label: 'ברבר שופ' },
  { id: 'cosmetics',     label: 'קוסמטיקה' },
  { id: 'other',         label: 'אחר' },
];

// ---------- helpers & logger ----------

function nowIso() {
  return new Date().toISOString();
}

function guessSupabaseRestUrl(): string {
  // לא כל גרסת לקוח חושפת URL, נעשה ניסיון "best effort"
  // supabase-js v2: client['rest']['url'] קיים בחלק מהבניות
  // בנוסף ננסה משתני סביבה נפוצים כדי שיהיה לוג ברור
  const anyClient = supabase as any;
  return (
    anyClient?.rest?.url ||
    anyClient?.storage?.storageUrl?.replace('/storage/v1', '') ||
    (import.meta as any)?.env?.VITE_SUPABASE_URL ||
    (globalThis as any)?.process?.env?.VITE_SUPABASE_URL ||
    'unknown'
  );
}

function makeClientBookingLink() {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `b-${hex}`;
}

function getRegUid() {
  try {
    const key = 'reg_uid';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = (crypto as any)?.randomUUID?.() || Math.random().toString(36).slice(2);
    localStorage.setItem(key, id);
    return id;
  } catch {
    return 'guest';
  }
}

// ---------- component ----------

export function BusinessStepTwo({ loading }: BusinessStepTwoProps) {
  const { updateStep, getStepData } = useRegistration();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const saved = getStepData(2);
  const [previewUrl, setPreviewUrl]   = useState<string | null>(saved?.logo_url || null);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState(() => ({
    name:        saved?.name || '',
    type:        saved?.type || '',
    description: saved?.description || '',
    address:     saved?.address || '',
    logo_path:   saved?.logo_path || '',
    logo_url:    saved?.logo_url || '',
  }));

  const [errors, setErrors] = useState({
    name: '', type: '', description: '', address: '', logo: ''
  });

  // ---------- validators ----------

  const validateForm = () => {
    const newErrors = { name: '', type: '', description: '', address: '', logo: '' };

    if (!formData.name) newErrors.name = 'שדה חובה';
    else if (formData.name.length < 2) newErrors.name = 'שם העסק חייב להכיל לפחות 2 תווים';
    else if (formData.name.length > 50) newErrors.name = 'שם העסק לא יכול להכיל יותר מ-50 תווים';

    if (!formData.type) newErrors.type = 'שדה חובה';

    if (formData.description && formData.description.length > 500)
      newErrors.description = 'התיאור לא יכול להכיל יותר מ-500 תווים';

    if (formData.address && formData.address.length > 100)
      newErrors.address = 'הכתובת לא יכולה להכיל יותר מ-100 תווים';

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // ---------- uploads ----------

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logo: 'הקובץ גדול מדי (מקסימום 5MB)' }));
      toast.error('הקובץ גדול מדי (מקסימום 5MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logo: 'ניתן להעלות רק קבצי תמונה' }));
      toast.error('ניתן להעלות רק קבצי תמונה');
      return;
    }

    console.groupCollapsed(`[BusinessStepTwo ${nowIso()}] Upload logo`);
    try {
      setErrors(prev => ({ ...prev, logo: '' }));
      setIsUploading(true);

      // preview
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);

      const regUid = getRegUid();
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const filePath = `registration/${regUid}/${fileName}`;

      const beforeUrl = guessSupabaseRestUrl();
      console.log('storage.upload.bucket = business_assets');
      console.log('storage.upload.filePath =', filePath);
      console.log('REST base (guess) =', beforeUrl);

      const { error: uploadError } = await supabase
        .storage
        .from('business_assets')
        .upload(filePath, file, { upsert: false });

      console.log('upload.error =', uploadError ?? null);
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('business_assets').getPublicUrl(filePath);
      const publicUrl = data.publicUrl;
      console.log('publicUrl =', publicUrl);

      setFormData(prev => ({ ...prev, logo_path: filePath, logo_url: publicUrl }));
      toast.success('הלוגו הועלה בהצלחה');
    } catch (err: any) {
      console.error('Upload error:', err);
      setErrors(prev => ({ ...prev, logo: err?.message || 'שגיאה בהעלאת הלוגו' }));
      toast.error(err?.message || 'שגיאה בהעלאת הלוגו');
    } finally {
      console.groupEnd();
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async () => {
    console.groupCollapsed(`[BusinessStepTwo ${nowIso()}] Remove logo`);
    try {
      if (formData.logo_path) {
        console.log('storage.remove', formData.logo_path);
        await supabase.storage.from('business_assets').remove([formData.logo_path]);
      }
    } catch (e) {
      console.warn('remove warning:', e);
    } finally {
      setPreviewUrl(null);
      setFormData(prev => ({ ...prev, logo_path: '', logo_url: '' }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      console.groupEnd();
    }
  };

  // ---------- submit ----------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.groupCollapsed(`[BusinessStepTwo ${nowIso()}] Submit flow`);
    try {
      console.log('Validation');
      const valid = validateForm();
      console.log('valid:', valid, 'errors:', errors);
      if (!valid || loading) {
        if (loading) console.log('blocked by loading:', loading);
        return;
      }

      // fetch user
      console.log('Auth.getUser()');
      const userStart = performance.now();
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      const userElapsed = Math.round(performance.now() - userStart);
      console.log('getUser.error =', userErr ?? null, 'elapsedMs =', userElapsed);
      console.log('getUser.user =', user ?? null);

      if (userErr) {
        toast.error(userErr.message || 'שגיאת התחברות');
        return;
      }
      if (!user) {
        toast.error('אנא התחבר תחילה');
        return;
      }

      const restUrl = guessSupabaseRestUrl();
      console.log('REST base (guess) =', restUrl);

      // ננסה לבנות לינק לקוח (לוג בלבד – ה-RPC מייצר בפועל)
      const debugBooking = makeClientBookingLink();
      console.log('debug booking_link (client-fallback example) =', debugBooking);

      // קריאה ל-RPC create_business
      console.log('RPC create_business() request');
      const rpcPayload = {
        p_name:              formData.name,
        p_business_category: formData.type,
        p_description:       formData.description || null,
        p_address:           formData.address || null,
        p_logo_url:          formData.logo_url || null,
      };
      console.log('RPC payload =', rpcPayload);

      const rpcStart = performance.now();
      const { data, error, status } = await supabase.rpc('create_business', rpcPayload);
      const rpcElapsed = Math.round(performance.now() - rpcStart);

      console.log('RPC response.status =', status);
      console.log('RPC response.elapsedMs =', rpcElapsed);
      console.log('RPC response.data =', data ?? null);
      console.log('RPC response.error =', error ?? null);

      if (error) {
        // הודעות נוחות לזיהוי:
        // PGRST202 – הפונקציה לא קיימת/לא נטענה
        // 42501 / RLS – בעיות הרשאות
        const msg = error.message || '';
        if (error.code === 'PGRST202' || msg.includes('Could not find the function')) {
          toast.error('RPC create_business לא קיים בסכימה. ודא שהפונקציה נוצרה ו-PostgREST רענן את הסכימה (notify pgrst, \'reload schema\').');
        } else if (msg.toLowerCase().includes('rls') || error.code === '42501') {
          toast.error('חסרות הרשאות (RLS). ודא שהמדיניות מאפשרת insert ל-businesses עם owner_id = auth.uid().');
        } else {
          toast.error(`שגיאה ב-RPC create_business: ${msg}`);
        }
        throw new Error(`RPC create_business failed: ${msg}`);
      }

      const businessId = data as string | null;
      if (!businessId) {
        toast.error('ה-RPC לא החזיר businessId');
        throw new Error('RPC returned null businessId');
      }

      // שמירה ל-localStorage + context
      localStorage.setItem('businessId', businessId);
      console.log('businessId stored in localStorage =', businessId);

      await updateStep(2, {
        name:        formData.name,
        type:        formData.type,
        description: formData.description,
        address:     formData.address,
        logo_path:   formData.logo_path,
        logo_url:    formData.logo_url,
        business_id: businessId,
      });

      toast.success('העסק נוצר בהצלחה!');
    } catch (err: any) {
      console.error('Submit error:', err);
      toast.error(err?.message || 'שגיאה בשמירת פרטי העסק');
    } finally {
      console.groupEnd();
    }
  };

  // ---------- UI ----------

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-7">
        {/* שם העסק */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 100 }}
        >
          <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-1">
            שם העסק <span className="text-red-500">*</span>
          </label>
          <input
            id="business-name"
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, name: e.target.value }));
              if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
            }}
            className={`block w-full px-4 py-3 text-right border ${
              errors.name
                ? 'border-red-300 bg-red-50/50 focus:border-red-500'
                : 'border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
            } rounded-xl focus:outline-none transition-colors text-sm`}
            placeholder="לדוגמה: סלון היופי של שרה"
            autoComplete="organization"
          />
          {errors.name && <p className="mt-1.5 text-[13px] text-red-600">{errors.name}</p>}
        </motion.div>

        {/* סוג העסק */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
        >
          <label htmlFor="business-type" className="block text-sm font-medium text-gray-700 mb-1">
            סוג העסק <span className="text-red-500">*</span>
          </label>
          <select
            id="business-type"
            value={formData.type}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, type: e.target.value }));
              if (errors.type) setErrors(prev => ({ ...prev, type: '' }));
            }}
            className={`block w-full px-4 py-3 text-right border ${
              errors.type
                ? 'border-red-300 bg-red-50/50 focus:border-red-500'
                : 'border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
            } rounded-xl focus:outline-none transition-colors text-sm`}
          >
            <option value="">בחר סוג עסק</option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          {errors.type && <p className="mt-1.5 text-[13px] text-red-600">{errors.type}</p>}
        </motion.div>

        {/* לוגו */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
        >
          <label className="block text-sm font-medium text-gray-700 mb-1">
            לוגו העסק <span className="text-gray-500 text-xs font-normal mr-2">(אופציונלי)</span>
          </label>
          <div className="mt-2">
            {previewUrl ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="relative inline-block group"
              >
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-gray-200">
                  <img src={previewUrl} alt="תצוגה מקדימה" className="w-full h-full object-cover" />
                </div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -left-2 p-1.5 bg-gray-900 text-white rounded-full active:scale-95 transition-transform"
                >
                  <X className="h-4 w-4" />
                </button>
              </motion.div>
            ) : (
              <label
                htmlFor="business-logo-input"
                className="flex flex-col items-center justify-center px-6 py-8 border border-dashed border-gray-300 rounded-xl hover:border-gray-400 transition-colors cursor-pointer bg-gray-50"
              >
                <div className="space-y-3 text-center">
                  {isUploading ? (
                    <>
                      <div className="mx-auto h-10 w-10 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      <p className="text-[15px] text-gray-600">מעלה קובץ...</p>
                    </>
                  ) : (
                    <>
                      <div className="mx-auto w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-gray-600 rounded-lg" />
                      </div>
                      <div>
                        <p className="text-[15px] font-medium text-gray-800">לחץ להעלאת לוגו</p>
                        <p className="text-[13px] text-gray-500 mt-1">PNG, JPG עד 5MB</p>
                      </div>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    id="business-logo-input"
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isUploading}
                  />
                </div>
              </label>
            )}
            {errors.logo && <p className="mt-1.5 text-[13px] text-red-600">{errors.logo}</p>}
          </div>
        </motion.div>

        {/* תיאור */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
        >
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            תיאור העסק <span className="text-gray-500 text-xs font-normal mr-2">(אופציונלי)</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, description: e.target.value }));
              if (errors.description) setErrors(prev => ({ ...prev, description: '' }));
            }}
            rows={4}
            className={`block w-full px-4 py-3 text-right border ${
              errors.description
                ? 'border-red-300 bg-red-50/50 focus:border-red-500'
                : 'border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
            } rounded-xl focus:outline-none transition-colors resize-none text-sm`}
            placeholder="ספר לנו קצת על העסק שלך..."
          />
          {errors.description && <p className="mt-1.5 text-[13px] text-red-600">{errors.description}</p>}
          <p className="mt-1.5 text-[13px] text-gray-500">התיאור יוצג ללקוחות בדף ההזמנות שלך</p>
        </motion.div>

        {/* כתובת */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
        >
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            כתובת העסק <span className="text-gray-500 text-xs font-normal mr-2">(אופציונלי)</span>
          </label>
          <textarea
            id="address"
            value={formData.address}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, address: e.target.value }));
              if (errors.address) setErrors(prev => ({ ...prev, address: '' }));
            }}
            rows={2}
            className={`block w-full px-4 py-3 text-right border ${
              errors.address
                ? 'border-red-300 bg-red-50/50 focus:border-red-500'
                : 'border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
            } rounded-xl focus:outline-none transition-colors resize-none text-sm`}
            placeholder="לדוגמה: רחוב הרצל 45, תל אביב"
          />
          {errors.address && <p className="mt-1.5 text-[13px] text-red-600">{errors.address}</p>}
          <p className="mt-1.5 text-[13px] text-gray-500">הכתובת תוצג ללקוחות בדף ההזמנות שלך</p>
        </motion.div>
      </div>

      {/* כפתור המשך */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="pt-8"
      >
        <button
          type="submit"
          disabled={loading || isUploading}
          className="w-full py-3 px-4 rounded-xl text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loading || isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>טוען...</span>
            </>
          ) : (
            <>
              <span>המשך לשלב הבא</span>
              <span>←</span>
            </>
          )}
        </button>
      </motion.div>
    </form>
  );
}

export default BusinessStepTwo;
